/**
 * @file     housingCost.ts
 * @purpose  The one convention for what a lender counts as housing cost in the
 *           GDS and TDS tests: principal and interest at the qualifying rate,
 *           property tax, heat, and half the strata fee, plus the defaults used
 *           when a figure is unknown.
 */

/** The share of a strata fee a lender counts toward housing cost. */
export const STRATA_INCLUSION = 0.5;

/** Monthly heat a lender assumes when the real figure is unknown. */
export const DEFAULT_HEAT_MONTHLY = 100;

/**
 * Blended Greater Vancouver effective residential property-tax rate, as a share
 * of price per year. Used when a listing or a visitor gives no tax figure.
 */
export const DEFAULT_PROPERTY_TAX_ANNUAL_RATE = 0.0035;

export interface HousingCostInput {
  /** Monthly principal and interest, normally at the qualifying rate. */
  principalAndInterest: number;
  /** Monthly property tax. Pass `defaultMonthlyPropertyTax(price)` when unknown. */
  propertyTaxMonthly: number;
  /** Monthly heat. Defaults to DEFAULT_HEAT_MONTHLY when omitted. */
  heatMonthly?: number;
  /** The full monthly strata fee; only STRATA_INCLUSION of it is counted. */
  strataMonthly?: number;
}

/** Monthly property tax at the default rate for a price. */
export function defaultMonthlyPropertyTax(price: number): number {
  return price > 0 ? (price * DEFAULT_PROPERTY_TAX_ANNUAL_RATE) / 12 : 0;
}

/** The GDS numerator. Add other monthly debt payments to it for TDS. */
export function qualifyingHousingCost(input: HousingCostInput): number {
  return (
    input.principalAndInterest +
    input.propertyTaxMonthly +
    (input.heatMonthly ?? DEFAULT_HEAT_MONTHLY) +
    (input.strataMonthly ?? 0) * STRATA_INCLUSION
  );
}
